import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { APP_CONFIGS } from '../services/sso';

const RECENT_SEARCHES_KEY = 'quantrolix-recent-searches';
const MAX_RECENT_SEARCHES = 5;
const DEBOUNCE_MS = 200;

/**
 * Static list of navigation pages with metadata.
 * Mirrors the routes defined in App.jsx and the sidebar menuItems.
 */
const PAGES = [
  { id: 'dashboard', title: 'Dashboard', subtitle: 'Overview and metrics', path: '/dashboard', icon: 'chart', shortcut: 'Ctrl+D' },
  { id: 'marketplace', title: 'Marketplace', subtitle: 'Browse products and deals', path: '/marketplace', icon: 'shop' },
  { id: 'scan', title: 'Scan', subtitle: 'Scan products by barcode', path: '/scan', icon: 'scan' },
  { id: 'ai-agents', title: 'AI Agents', subtitle: 'Monitor and manage AI agents', path: '/ai-agents', icon: 'bot' },
  { id: 'video-generator', title: 'Video Generator', subtitle: 'Create product videos', path: '/video-generator', icon: 'video' },
  { id: 'analytics', title: 'Analytics', subtitle: 'Revenue and performance analytics', path: '/analytics', icon: 'analytics' },
  { id: 'scrapers', title: 'Scrapers', subtitle: 'Marketplace scraper status', path: '/scrapers', icon: 'refresh' },
  { id: 'seller-accounts', title: 'Seller Accounts', subtitle: 'Manage seller accounts', path: '/seller-accounts', icon: 'users' },
  { id: 'monitoring', title: 'Monitoring', subtitle: 'System health monitoring', path: '/monitoring', icon: 'monitor' },
  { id: 'apps', title: 'App Marketplace', subtitle: 'Browse Quantrolix apps', path: '/apps', icon: 'grid' },
  { id: 'billing', title: 'Billing', subtitle: 'Manage subscription and billing', path: '/billing', icon: 'credit-card' },
  { id: 'support', title: 'Support', subtitle: 'Get help and contact support', path: '/support', icon: 'headphones' },
  { id: 'help', title: 'Help & Guides', subtitle: 'Documentation and tutorials', path: '/help', icon: 'book' },
  { id: 'activity', title: 'Activity Feed', subtitle: 'Ecosystem activity and events', path: '/activity', icon: 'analytics' },
  { id: 'settings', title: 'Settings', subtitle: 'Account and app settings', path: '/settings', icon: 'settings', shortcut: 'Ctrl+,' },
];

/**
 * Static list of Quantrolix apps derived from SSO configs
 */
const APPS = Object.values(APP_CONFIGS).map((app) => ({
  id: app.id,
  title: app.name,
  subtitle: `Launch ${app.name}`,
  icon: app.icon,
  appId: app.id,
}));

/**
 * Quick actions available from the search palette
 */
const ACTIONS = [
  { id: 'toggle-theme', title: 'Toggle Theme', subtitle: 'Switch between dark and light mode', icon: 'sun', actionType: 'theme' },
  { id: 'open-settings', title: 'Open Settings', subtitle: 'Go to settings page', icon: 'settings', actionType: 'navigate', path: '/settings' },
  { id: 'view-billing', title: 'View Billing', subtitle: 'Check subscription and invoices', icon: 'credit-card', actionType: 'navigate', path: '/billing' },
  { id: 'go-dashboard', title: 'Go to Dashboard', subtitle: 'Return to main dashboard', icon: 'chart', actionType: 'navigate', path: '/dashboard' },
  { id: 'view-analytics', title: 'View Analytics', subtitle: 'Open analytics reports', icon: 'analytics', actionType: 'navigate', path: '/analytics' },
  { id: 'view-support', title: 'Contact Support', subtitle: 'Open the support center', icon: 'headphones', actionType: 'navigate', path: '/support' },
];

function getRecentSearches() {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query) {
  if (!query || query.trim().length === 0) return;
  try {
    const existing = getRecentSearches();
    const updated = [query.trim(), ...existing.filter((s) => s !== query.trim())].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable
  }
}

function clearRecentSearches() {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // localStorage unavailable
  }
}

/**
 * Filter items by query, returning items whose title or subtitle contain all query words
 */
function filterByQuery(items, query) {
  if (!query || query.trim().length === 0) return items;
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    const text = `${item.title} ${item.subtitle || ''}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
}

/**
 * Custom hook for global search with keyboard navigation.
 *
 * Provides:
 * - Cmd/Ctrl+K to open/close the search palette
 * - Cmd/Ctrl+, to navigate to settings
 * - Cmd/Ctrl+D to navigate to dashboard
 * - Debounced search input with instant result filtering
 * - Arrow key navigation, Enter to select, Escape to close
 * - Fetches tasks from Comm Hub for task search
 * - Recent searches stored in localStorage (max 5)
 */
export function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(getRecentSearches);
  const debounceTimer = useRef(null);
  const navigate = useNavigate();
  const { setTheme, resolvedTheme } = useTheme();

  // Debounce the query
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery]);

  // Fetch tasks from Comm Hub when search is opened
  useEffect(() => {
    if (!isOpen) return;

    const fetchTasks = async () => {
      setTasksLoading(true);
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        const res = await fetch('https://apps.quantrolix.ai/comm-hub/api/tasks', { signal: controller.signal });
        clearTimeout(timer);
        if (res.ok) {
          const data = await res.json();
          const taskList = Array.isArray(data) ? data : (data.tasks || []);
          setTasks(taskList.map((t) => ({
            id: `task-${t.id}`,
            title: t.title || `Task #${t.id}`,
            subtitle: `${t.status || 'pending'} ${t.project ? '- ' + t.project : ''}`.trim(),
            icon: 'task',
            taskData: t,
          })));
        }
      } catch {
        // Comm Hub not available - that is okay
        setTasks([]);
      } finally {
        setTasksLoading(false);
      }
    };

    fetchTasks();
  }, [isOpen]);

  // Build grouped, filtered results
  const results = useMemo(() => {
    const groups = [];

    const filteredPages = filterByQuery(PAGES, debouncedQuery);
    if (filteredPages.length > 0) {
      groups.push({ category: 'Pages', items: filteredPages });
    }

    const filteredApps = filterByQuery(APPS, debouncedQuery);
    if (filteredApps.length > 0) {
      groups.push({ category: 'Apps', items: filteredApps });
    }

    const filteredTasks = filterByQuery(tasks, debouncedQuery);
    if (filteredTasks.length > 0) {
      groups.push({ category: 'Tasks', items: filteredTasks.slice(0, 8) });
    }

    const filteredActions = filterByQuery(ACTIONS, debouncedQuery);
    if (filteredActions.length > 0) {
      groups.push({ category: 'Actions', items: filteredActions });
    }

    return groups;
  }, [debouncedQuery, tasks]);

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => results.flatMap((g) => g.items), [results]);

  // Open / Close
  const open = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setDebouncedQuery('');
    setSelectedIndex(0);
    setRecentSearches(getRecentSearches());
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setSelectedIndex(0);
  }, []);

  // Execute the selected result
  const executeItem = useCallback((item) => {
    if (!item) return;

    // Save search if there was a query
    if (query.trim()) {
      saveRecentSearch(query.trim());
    }

    // Determine action based on item type
    if (item.path) {
      // Navigate to page
      navigate(item.path);
      close();
      return;
    }

    if (item.appId) {
      // Launch app - import dynamically to avoid circular deps
      import('../services/sso').then(({ ssoService }) => {
        ssoService.launchApp(item.appId, true);
      });
      close();
      return;
    }

    if (item.actionType === 'theme') {
      const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
      setTheme(newTheme);
      close();
      return;
    }

    if (item.actionType === 'navigate' && item.path) {
      navigate(item.path);
      close();
      return;
    }

    // Task items - for now, just close
    close();
  }, [query, navigate, close, resolvedTheme, setTheme]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatItems[selectedIndex]) {
          executeItem(flatItems[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  }, [isOpen, flatItems, selectedIndex, executeItem, close]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Cmd+K or Ctrl+K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          close();
        } else {
          open();
        }
        return;
      }

      // Ctrl+, to open settings (only when search is not open to avoid conflicts)
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        navigate('/settings');
        if (isOpen) close();
        return;
      }

      // Ctrl+D to go to dashboard
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        navigate('/dashboard');
        if (isOpen) close();
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, open, close, navigate]);

  const handleClearRecentSearches = useCallback(() => {
    clearRecentSearches();
    setRecentSearches([]);
  }, []);

  const handleSelectRecent = useCallback((search) => {
    setQuery(search);
    setDebouncedQuery(search);
  }, []);

  return {
    isOpen,
    open,
    close,
    query,
    setQuery,
    debouncedQuery,
    results,
    flatItems,
    selectedIndex,
    setSelectedIndex,
    handleKeyDown,
    executeItem,
    tasksLoading,
    recentSearches,
    handleClearRecentSearches,
    handleSelectRecent,
  };
}

export default useGlobalSearch;
